import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'

import * as React from 'react'

import { cn } from './utils'

/** Buttons: 32px tall with 8px corners (the same height as inputs), 14px medium label; primary is IWS emerald, secondary a grey fill. */
const buttonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** The main action on a page. */
        default: 'bg-primary text-primary-foreground shadow-button hover:bg-primary-hover',
        /** Same as default; kept for call sites that asked for a dark "+ New …" action. */
        dark: 'bg-primary text-primary-foreground shadow-button hover:bg-primary-hover',
        /** Grey fill ("Add product", "Discard"). */
        secondary: 'bg-fill text-ink hover:bg-fill-hover',
        /** White with a hairline ("Manage", "Cancel"). */
        outline: 'border border-input bg-card text-ink hover:bg-subdued',
        ghost: 'text-ink hover:bg-fill',
        /** Grey fill; the old brand tint has no Shopify equivalent. */
        soft: 'bg-fill text-ink hover:bg-fill-hover',
        destructive: 'bg-destructive text-destructive-foreground shadow-button hover:bg-error-dark',
        link: 'h-auto px-0 text-link underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-3',
        sm: 'h-7 px-2.5 text-xs',
        lg: 'h-9 px-4',
        icon: 'size-8',
        'icon-sm': 'size-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
